import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { X, Plus, Trash2, Layers, Save, Search } from "lucide-react";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import Loader from "../../loader/Loader";
import ReactDOM from "react-dom";
// ─── Custom Searchable Dropdown ───────────────────────────────────────────────
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null); // ✅ NEW: ref for the portal dropdown

  const selectedOption = options.find((o) => o.value === value);

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setIsOpen(true);
    setSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // ✅ Also ignore clicks inside the portal dropdown
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        ref={triggerRef}
        className={`w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white flex justify-between items-center ${
          disabled
            ? "opacity-60 cursor-not-allowed bg-slate-50"
            : "cursor-pointer"
        }`}
        onClick={() => {
          if (!disabled) isOpen ? setIsOpen(false) : openDropdown();
        }}
      >
        <span
          className={`truncate pr-2 ${selectedOption ? "text-slate-800 font-medium" : "text-slate-400"}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef} // ✅ Attach ref here so clicks inside are recognized
            style={dropdownStyle}
            className="bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col overflow-hidden"
          >
            <div className="bg-slate-50 p-2 border-b border-slate-100">
              <input
                type="text"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Type to search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((o) => (
                  <div
                    key={o.value}
                    className="px-3 py-2.5 text-sm hover:bg-purple-50 cursor-pointer text-slate-700 border-b border-slate-50 last:border-0"
                    onClick={() => {
                      onChange(o.value);
                      setIsOpen(false);
                    }}
                  >
                    {o.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-center text-slate-400">
                  No results found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AddProductBOM = ({ onClose, refreshData, bomToEdit }) => {
  const currentUserID = useSelector(selectUserID);
  const [isLoading, setIsLoading] = useState(false);
  const [regularProducts, setRegularProducts] = useState([]);
  const [bomProducts, setBomProducts] = useState([]);

  // ✅ Track bomProductIds already assigned to an existing BOM
  const [usedBomProductIds, setUsedBomProductIds] = useState([]);

  // BOM header state
  const [bomProductId, setBomProductId] = useState("");
  const [description, setDescription] = useState("");
  const [components, setComponents] = useState([]);

  // Ingredient form state
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [selectedUomId, setSelectedUomId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [ingredientUoms, setIngredientUoms] = useState([]);

  const fetchProductsByType = useCallback(async () => {
    setIsLoading(true);
    try {
      const [regularRes, bomRes, existingBomsRes] = await Promise.all([
        axios.get(
          `${domain}/api/Products/by-item-type-category?category=Regular`,
        ),
        axios.get(`${domain}/api/Products/by-item-type-category?category=BOM`),
        // ✅ Fetch all existing BOMs to detect which output products are already used
        axios.get(`${domain}/api/ProductBom`),
      ]);

      setRegularProducts(regularRes.data);
      setBomProducts(bomRes.data);

      // ✅ Exclude the BOM being edited so it can keep its own output product
      const usedIds = existingBomsRes.data
        .filter((b) => !bomToEdit || b.id !== bomToEdit.id)
        .map((b) => b.bomProductId);
      setUsedBomProductIds(usedIds);
    } catch (err) {
      toast.error("Failed to load products.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [bomToEdit]);

  const fetchBomForEdit = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${domain}/api/ProductBom/${id}`);
      const data = res.data;
      setBomProductId(data.bomProductId.toString());
      setDescription(data.description || "");
      setComponents(
        data.components.map((c) => ({
          productId: c.componentProductId,
          productName: c.componentProductName,
          brandName: c.componentProductBrandName,
          itemCode: c.componentItemCode,
          uomId: c.uomId,
          uomName: c.uomName,
          quantity: c.quantity,
        })),
      );
    } catch (err) {
      toast.error("Failed to load BOM for editing.");
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [onClose]);

  useEffect(() => {
    fetchProductsByType();
  }, [fetchProductsByType]);

  useEffect(() => {
    if (bomToEdit) {
      fetchBomForEdit(bomToEdit.id);
    }
  }, [fetchBomForEdit, bomToEdit]);

  const handleIngredientChange = async (id) => {
    setSelectedIngredientId(id);
    setSelectedUomId("");
    setIngredientUoms([]);
    if (id) {
      try {
        const res = await axios.get(
          `${domain}/api/Products/${id}/pricing-uoms`,
        );
        setIngredientUoms(res.data);
      } catch (err) {
        toast.error("Failed to load UOMs for this ingredient.");
      }
    }
  };

  const handleAddIngredient = () => {
    if (!selectedIngredientId || !selectedUomId || !quantity || quantity <= 0) {
      toast.warning("Please fill all ingredient fields.");
      return;
    }
    const product = regularProducts.find(
      (p) => p.id === Number(selectedIngredientId),
    );
    const uom = ingredientUoms.find((u) => u.id === Number(selectedUomId));
    if (components.some((c) => c.productId === product.id)) {
      toast.warning("This ingredient is already in the list.");
      return;
    }
    setComponents([
      ...components,
      {
        productId: product.id,
        productName: product.productName,
        itemCode: product.itemCode,
        uomId: uom.id,
        uomName: uom.name,
        quantity: Number(quantity),
        brandName: product.brandName
      },
    ]);
    setSelectedIngredientId("");
    setSelectedUomId("");
    setQuantity("");
    setIngredientUoms([]);
  };

  const handleRemoveIngredient = (productId) => {
    setComponents(components.filter((c) => c.productId !== productId));
  };

  const handleSave = async () => {
    if (!bomProductId) {
      toast.warning("Please select the Output Product.");
      return;
    }
    if (components.length === 0) {
      toast.warning("Please add at least one ingredient.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        description: description.trim(),
        bomProductId: Number(bomProductId),
        isActive: true,
        components: components.map((c) => ({
          componentProductId: c.productId,
          quantity: c.quantity,
          uomId: c.uomId,
          sortOrder: 0,
        })),
        userID: currentUserID,
      };

      if (bomToEdit) {
        const original = await axios.get(
          `${domain}/api/ProductBom/${bomToEdit.id}`,
        );
        payload.isActive = original.data.isActive;
        await axios.put(
          `${domain}/api/ProductBom/${bomToEdit.id}/full-update`,
          payload,
        );
        toast.success("BOM updated successfully!");
      } else {
        await axios.post(`${domain}/api/ProductBom`, payload);
        toast.success("BOM created successfully!");
      }
      refreshData();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save BOM.");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Only show BOM products not already assigned to another BOM
  const bomOptions = useMemo(
    () =>
      bomProducts
        .filter((p) => !usedBomProductIds.includes(p.id))
        .map((p) => ({
          value: p.id,
          label: `${p.productName} (${p.itemCode}) - ${p.itemTypeName || "BOM"}`,
        })),
    [bomProducts, usedBomProductIds],
  );

  // Filter out the selected output product AND already-added components
  const availableIngredients = regularProducts.filter(
    (p) =>
      p.id !== Number(bomProductId) &&
      !components.some((c) => c.productId === p.id),
  );

  const ingredientOptions = useMemo(
    () =>
      availableIngredients.map((p) => ({
        value: p.id,
        label: `${p.productName} ${p.brandName ? `(${p.brandName})` : ""}`,
      })),
    [availableIngredients],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-xl">
              <Layers size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {bomToEdit ? "Edit BOM" : "Add New BOM"}
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                {bomToEdit
                  ? "Modify recipe"
                  : "Create a bundle from your existing products"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-xl transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && <Loader />}

          {/* ── STEP 1: BOM Details ── */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              1. BOM Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white border border-slate-200 p-4 rounded-2xl shadow-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {bomToEdit ? (
                    "Output Product"
                  ) : (
                    <>
                      Output Product <span className="text-red-500">*</span>
                    </>
                  )}
                </label>

                {/* Read-only when editing — output product cannot be changed */}
                {bomToEdit ? (
                  <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 font-semibold cursor-not-allowed">
                    {bomProducts.find((p) => p.id === Number(bomProductId))
                      ?.productName ||
                      bomToEdit?.bomName ||
                      "Loading..."}
                  </div>
                ) : (
                  // ✅ bomOptions already excludes products used by other BOMs
                  <SearchableSelect
                    options={bomOptions}
                    value={bomProductId}
                    onChange={(val) => setBomProductId(val)}
                    placeholder="-- Search / Select BOM Product --"
                  />
                )}

                {!bomToEdit && (
                  <p className="text-xs text-slate-400 mt-1">
                    Output must be a BOM type product only
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Special mixture with extra cement"
                />
              </div>
            </div>
          </div>

          {/* ── STEP 2: Add Ingredients ── */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              2. Add Items (Regular Products Only)
            </h3>
            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-5 relative">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Items <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    options={ingredientOptions}
                    value={selectedIngredientId}
                    onChange={handleIngredientChange}
                    placeholder="Search / Select Regular Product..."
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Unit (UOM) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedUomId}
                    onChange={(e) => setSelectedUomId(e.target.value)}
                    disabled={!selectedIngredientId}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                  >
                    <option value="">Select UOM...</option>
                    {ingredientUoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.type})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. 2"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    onClick={handleAddIngredient}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Plus size={16} /> Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── STEP 3: Ingredients List ── */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              3. BOM Item Preview
            </h3>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase">
                      Product
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-center">
                      Qty
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-center">
                      Unit
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-right">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {components.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-5 py-8 text-center text-slate-400 italic"
                      >
                        No ingredients added yet.
                      </td>
                    </tr>
                  ) : (
                    components.map((c) => (
                      <tr
                        key={c.productId}
                        className="hover:bg-slate-50 transition"
                      >
                        <td className="px-5 py-3">
                          <div>
                            <p className="font-semibold text-slate-700">
                              {`${c.productName} ${c.brandName ? `(${c.brandName})` : ""}`}
                            </p>
                            <p className="text-xs text-slate-400 font-mono">
                              {c.itemCode}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-center font-bold text-slate-800 bg-slate-50">
                          {c.quantity}
                        </td>
                        <td className="px-5 py-3 text-center text-slate-600">
                          {c.uomName}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => handleRemoveIngredient(c.productId)}
                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold bg-white border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-100 shadow-sm transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || components.length === 0 || !bomProductId}
            className="px-6 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-sm transition disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} /> {bomToEdit ? "Update BOM" : "Save BOM"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProductBOM;
